import { AfterViewInit, Component, Input, OnChanges, ViewChild, SimpleChanges, Optional } from '@angular/core';
import { Subscription, combineLatest, of } from 'rxjs';
import { map } from 'rxjs/operators';

import { VbBaseMenuComponent } from './shared/VbBaseMenu.Component';

import { VbPlaybackManagerComponent } from './VbPlaybackManager.Component';
import { VideogularComponent } from './videogular/Videogular.Component';
import { VbPlaybackRateButtonComponent } from './VbPlaybackRateButton.Component';
import { IPlayerSettingsMenu, MENU_ITEM_ID } from './IPlayerSettingsMenu';
import { IVbSource } from './IVbSource';

import styles from './VbPlayerSettingsMenu.Component.module.less';
import { ElementRef } from '@angular/core';

@Component({
	selector: 'vb-player-settings-menu',
	host: {
		'[class]': 'styles.menuButtonContainer'
	},
	template: `
		<div dropdown
			#dropdown="bs-dropdown"
			placement="top right"
			[dropup]="true">

			<button
				[ngClass]="[styles.menuButton, styles.glyphicons, styles.cogwheel]"
				type="button"
				dropdownToggle
				vbControlBarFocus
				id="buttonSettingsMenu"
				aria-controls="dropdownSettingsmenu"
				[attr.aria-label]="translations.playerSettingsMenu"
				[tooltip]="translations.playerSettingsMenu"
				(click)="onSettingClick()">
			</button>

			<ul
				[ngClass]="styles.dropdownMenu"
				class="playerSettingsMenu"
				*dropdownMenu
				#vbDropdownMenu
				id="dropdownSettingsmenu"
				role="menu"
				aria-labelledby="buttonSettingsMenu"
				tabindex="0">
				<li *ngFor="let menuItem of menuItems;" role="menuitem">
					<button
						type="button"
						class="dropdown-item btn-link"
						(click)="onMenuItemClick(menuItem)"
						[attr.id]="'buttonMenu'+ menuItem.id"
						[attr.aria-controls]="'dropdown' + menuItem.id + 'Menu'"
						[ngClass]="[
							styles.menuItem,
							menuItem.hasSubmenu ? styles.hasSubmenu : ''
						]">
						<span [ngClass]="styles.menuItemLabel">{{ menuItem.label }}</span>

						<span [ngClass]="styles.menuItemContent">{{ menuItem.content }}</span>
					</button>
				</li>
			</ul>
		</div>
		<vb-playback-rate-button
			#vbPlaybackRateComp
			[hidden]="true"
			[parentElement]="elementRef"
			[activeSource]="vgApi.activeSource"
			[playbackOptions]="playbackOptions"
			[activateDropdown]="activeMenuId === MenuItemIds.PlaybackRate"
			[translations]="translations"
			(menuCollapsed)="resurfacePrimaryMenu($event.closeParent)">
		</vb-playback-rate-button>
		<vb-playback-menu-button
			[hidden]="true"
			[parentElement]="elementRef"
			[playbackManager]="playbackManager"
			[translations]="translations"
			[activateDropdown]="activeMenuId === MenuItemIds.PlaybackSource"
			(menuCollapsed)="resurfacePrimaryMenu($event.closeParent)">
		</vb-playback-menu-button>
		<vb-hotkeys-help
			*ngIf="!disableHotkeys"
			[hidden]="true"
			[parentElement]="elementRef"
			[translations]="translations"
			[activateDropdown]="activeMenuId === MenuItemIds.Hotkeys"
			(menuCollapsed)="resurfacePrimaryMenu($event.closeParent)">
		</vb-hotkeys-help>
	`
})
export class VbPlayerSettingsMenuComponent extends VbBaseMenuComponent implements OnChanges, AfterViewInit {
	@Input() public isLive: boolean;
	@Input() public disableHotkeys: boolean;
	@Input() public translations: any;
	@Input() public playbackManager: VbPlaybackManagerComponent;
	@Input() public activeSource: any;
	@Input() public playbackOptions: IVbSource[];
	@Input() public toggleSettingsMenu: boolean;

	@ViewChild('vbPlaybackRateComp')
	public vbPlaybackRateComp: VbPlaybackRateButtonComponent;

	public menuItems: IPlayerSettingsMenu[];
	public activeMenuId: MENU_ITEM_ID = MENU_ITEM_ID.None;

	public readonly styles = { ...this.menuButtonStyles, ...styles };
	public readonly MenuItemIds = MENU_ITEM_ID;

	private context: any = {};
	private subscription: Subscription;
	constructor(
		public elementRef: ElementRef,
		public vgApi: VideogularComponent
	) {
		super();
	}

	public ngOnChanges(changes: SimpleChanges): void {
		if (changes.isLive) {
			this.shapeMenuItems();
		}

		if (changes.toggleSettingsMenu && !changes.toggleSettingsMenu.firstChange) {
			this.toggleDropdown();
		}
	}

	public ngAfterViewInit(): void {
		super.ngAfterViewInit();
		this.subscription = combineLatest([
			this.vbPlaybackRateComp?.playbackRateEnabled$ ?? of(false),
			this.vgApi.vgActiveSourceChange,
			this.playbackManager.context$
		]).pipe(
			map(([playbackRateEnabled, { $source }, { supportedPlaybackOptions }]) => {
				return {
					playbackRateEnabled,
					activeSource: $source,
					supportedPlaybackOptions
				};
			})
		).subscribe(context => {
			this.context = context;
			this.shapeMenuItems();
		});
	}

	public ngOnDestroy(): void {
		super.ngOnDestroy();
		this.subscription?.unsubscribe();
	}

	public onMenuItemClick(menu: IPlayerSettingsMenu): void {
		if (!menu.hasSubmenu) {
			return;
		}
		this.dropdown.hide();
		this.activeMenuId = menu.id;
	}

	private shapeMenuItems(): void {
		this.menuItems = [
			!this.isLive && this.context.playbackRateEnabled && {
				id: MENU_ITEM_ID.PlaybackRate,
				label: this.translations.playbackSpeed,
				content: this.vgApi.playback === 1 ? this.translations.normal : this.vgApi.playback + 'x',
				hasSubmenu: true
			},
			this.context.activeSource && {
				id: MENU_ITEM_ID.PlaybackSource,
				label: this.translations.playbackSource,
				content: this.context.activeSource?.label,
				hasSubmenu: this.context.supportedPlaybackOptions?.length > 1
			},
			this.context.activeSource && !this.disableHotkeys && {
				id: MENU_ITEM_ID.Hotkeys,
				label: this.translations.keyboardShortcuts,
				hasSubmenu: true
			}
		].filter(Boolean);
	}

	public resurfacePrimaryMenu(closeParent?: boolean): void {
		this.activeMenuId = MENU_ITEM_ID.None;
		if (closeParent) {
			return;
		}
		setTimeout(() => {
			this.shapeMenuItems();
			this.dropdown.show();
		}, 100);
	}

	public onSettingClick(): void {
		this.activeMenuId = MENU_ITEM_ID.None;
	}

	public toggleDropdown(): void {
		if(!this.dropdown) {
			return;
		}

		if (this.activeMenuId !== MENU_ITEM_ID.None) {
			this.onSettingClick();
			return;
		}
		super.toggleDropdown();
	}
}
